import {
  css, type SerializedStyles,
} from '@emotion/react';
import {
  type FC, type ReactNode,
} from 'react';
import { ButtonSize } from '~/_shared/baseComponents/buttons';
import {
  ButtonComponent, type ButtonProps,
} from '~/_shared/baseComponents/buttons/button/button.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { useElementDimensions } from '~/_shared/utils/hooks/useElementDimensions';
import { ScrollbarType } from '../scrollbar/scrollbar.component';

const MIN_WIDTH_TO_COLLAPSE_BUTTONS = 800;
const COLLAPSED_BUTTONS_WIDTH = 31;

const rootStyle = css({
  transform: 'translateX(0)',
});

const containerStyle = css({
  cursor: 'default',
  display: 'flex',
  padding: 10,
  position: 'relative',
  maxWidth: '100%',
});

const contentStyle = css({
  flex: '1 1 auto',
  display: 'flex',
  flexWrap: 'wrap',
  minWidth: 0,
});

const buttonWrapperStyle = ({ isCollapsed }: { isCollapsed?: boolean }) => css({
  flexShrink: 0,
  marginLeft: 4,
  width: isCollapsed ? COLLAPSED_BUTTONS_WIDTH : undefined,
});

const buttonsWrapperStyle = css({
  position: 'sticky',
  top: 10,
  right: 10,
  display: 'flex',
  flexDirection: 'column',
});

type ScrollAndStickyHorizontalSplitComponentProps = Readonly<{
  additionalContainerStyles?: SerializedStyles;
  maximumHeight?: number;
  children?: ReactNode;
  buttons: (Omit<ButtonProps, 'text'> & { label: string })[];
}>;

export const ScrollAndStickyHorizontalSplitComponent: FC<ScrollAndStickyHorizontalSplitComponentProps> = (props) => {
  const { ref, width } = useElementDimensions();

  const collapseButtons = width !== undefined && width <= MIN_WIDTH_TO_COLLAPSE_BUTTONS;

  return (
    <div
      ref={ref}
      css={rootStyle}
    >
      <OverlayScrollbarComponent
        type={ScrollbarType.Vertical}
        maxHeight={props.maximumHeight || 100}
      >
        <div css={[containerStyle, props.additionalContainerStyles]}>
          <div css={contentStyle}>
            {props.children}
          </div>
          <div css={buttonWrapperStyle({ isCollapsed: collapseButtons })}>
            <div css={buttonsWrapperStyle}>
              {props.buttons.map((button, index) => (
                <div key={index}>
                  <TooltipComponent tooltipContent={collapseButtons ? button.label : ''}>
                    <ButtonComponent
                      {...button}
                      prefixIconStyle={css(collapseButtons ? { marginRight: 0 } : {})}
                      size={ButtonSize.Small}
                      text={collapseButtons ? '' : button.label}
                    />
                  </TooltipComponent>
                </div>
              ))}
            </div>
          </div>
        </div>
      </OverlayScrollbarComponent>
    </div>
  );
};
